"use client";

import { useEffect, useRef, useState } from "react";

function easeOutCubic(t: number): number {
  return 1 - Math.pow(1 - t, 3);
}

/**
 * Tweent eine Zahl sanft zu einem neuen Zielwert, statt sie hart
 * springen zu lassen — gedacht für UI, die auf Nutzerinteraktion (z.B.
 * einen Toggle) reagiert und dabei sichtbar "lebendig" wirken soll.
 * Respektiert prefers-reduced-motion.
 */
export function useAnimatedNumber(target: number, duration = 500): number {
  const [value, setValue] = useState(target);
  const fromRef = useRef(target);
  const rafRef = useRef<number | null>(null);

  useEffect(() => {
    const prefersReducedMotion =
      typeof window !== "undefined" &&
      window.matchMedia("(prefers-reduced-motion: reduce)").matches;

    const from = fromRef.current;
    const delta = target - from;
    if (delta === 0) return;

    const effectiveDuration = prefersReducedMotion ? 0 : duration;
    const start = performance.now();
    if (rafRef.current) cancelAnimationFrame(rafRef.current);

    function tick(now: number) {
      const elapsed = now - start;
      const t = effectiveDuration === 0 ? 1 : Math.min(1, elapsed / effectiveDuration);
      setValue(from + delta * easeOutCubic(t));
      if (t < 1) {
        rafRef.current = requestAnimationFrame(tick);
      } else {
        fromRef.current = target;
      }
    }
    rafRef.current = requestAnimationFrame(tick);

    return () => {
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
    };
  }, [target, duration]);

  return value;
}
